import {KafkaEvent, Loglevel, SourceLayer} from "../Interfaces/IKafkaEvents";
import {GlobalDictionary} from "../../../Utils/globalDictionary";
import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import {deepCopy} from "../../../Utils/transformer";
import {getLogCounts} from "../BackendFacade/apiCalls";

export async function buildSourceTreeUsingEventCountApi(dateFrom, dateTo, projectId) : Promise<SourceLayer>
{
    let eventCounts = {
        "Info": {
            "someTopic": {
                "someSource": 56,
                "someSource/a": 56,
                "someSource/b": 56,
                "someSource/a/c": 56,
            }
        }
    };

    const data = await getLogCounts(projectId, dateFrom, dateTo);
    if (data.success) {
        eventCounts = data.data.value;
    }
    else {
        console.error(data)
    }

    let rootSourceLayer: SourceLayer = createNewSourceLayer("LogRoot", "LogRoot", null);

    for (const logLevel in eventCounts) {
        for (const topic in eventCounts[logLevel]) {
            for (const source in eventCounts[logLevel][topic]) {
                // if (source.toLowerCase().indexOf("draeger.com/") == -1) continue;
                const sourcePolished = source.replace("https://draeger.com/", "");
                const leaf = recursiveSourceLayer(rootSourceLayer, sourcePolished);

                // only leaves (i.e. the lowest level of children with nothing more below) have event-counts.
                // to get the sum of non-leaf elements with children, use "countEventsInChildren()"
                leaf.amountOfEvents[logLevel] = eventCounts[logLevel][topic][source];
            }
        }
    }

    while (rootSourceLayer.children.length == 1) rootSourceLayer = rootSourceLayer.children[0];

    rootSourceLayer.parent = null; // "delete"/"cut-off" upper parts of the tree that we dont need anymore

    // reduce depth of layers
    reduceLayerDepth(rootSourceLayer);

    await replaceModuleInstanceIdsWithNames(rootSourceLayer);

    return rootSourceLayer;
}

export function countEventsInChildren(layer: SourceLayer) {
    let total = deepCopy(layer.amountOfEvents);

    for (let child of layer.children) {
        let sum = countEventsInChildren(child);
        for (let logLevel in sum) {
            if (total[logLevel] == null) total[logLevel] = 0;
            total[logLevel] += sum[logLevel];
        }
    }

    return total;
}

export function getLineage(layer: SourceLayer): SourceLayer[] {
    const res = [layer];
    while (layer.parent != null)
    {
        layer = layer.parent;
        res.push(layer);
    }
    return res.reverse();
}

function createNewSourceLayer(displayString: string, rawSource: string, parent: SourceLayer): SourceLayer {
    return {
        displayString,
        rawSource,
        parent: parent,
        children: [],
        amountOfEvents: {}
    };
}

function recursiveSourceLayer(parentLayer: SourceLayer, sourceString: string) {
    let [nextLayerName, ...rest] = sourceString.split("/")
    let remainingSource = rest.join("/")

    let lowestChild;

    const existingChild = parentLayer.children.find((layer) => layer.rawSource.toLowerCase() === nextLayerName.toLowerCase());
    if (existingChild == null) {
        lowestChild = createNewSourceLayer(nextLayerName, nextLayerName, parentLayer);
        parentLayer.children.push(lowestChild);
    }
    else lowestChild = existingChild;

    if (remainingSource != "") {
        lowestChild = recursiveSourceLayer(parentLayer.children.find((layer) => layer.rawSource.toLowerCase() === nextLayerName.toLowerCase()), remainingSource)
    }

    return lowestChild;
}

/**
 * Replace the Ids in the source tree with the display names
 * @param layers
 */
async function replaceModuleInstanceIdsWithNames(layers: SourceLayer) {
    const moduleInstances = GlobalDictionary.get("kafkaModuleInstances");
    if(!moduleInstances) {
        console.error("Module instances not found in global dictionary");
        throw new Error("Module instances not found in global dictionary");
    }

    // Define a regular expression pattern to match alphanumeric strings that include at least one number
    const idPattern = /\b[a-zA-Z]*[0-9][a-zA-Z0-9]*\b/g;

    for (const layer of layers.children) {

        // Search for IDs in the layer name using the regular expression pattern
        const matches = layer.rawSource.match(idPattern);
        if (matches) {
            for (const match of matches) {
                const moduleInstance = moduleInstances.find((instance: {
                    linkedId: { projectId: string; };
                }) => match === instance.linkedId.projectId);
                if (moduleInstance) {
                    layer.displayString = layer.displayString.replace(new RegExp(`\\b${match}\\b`, 'g'), moduleInstance.displayName);
                }
            }
        }

        // Perform similar replacement for module instance IDs

        // Replace project IDs with project names if needed
        if (HubPathRouting.currentProject) {
            const currentProjectId = HubPathRouting.currentProject.id;
            const currentProjectName = HubPathRouting.currentProject.displayName;
            layer.displayString = layer.displayString.replace(new RegExp(`\\b${currentProjectId}\\b`, 'g'), currentProjectName);
        } else {
            const projects = await GlobalDictionary.getProjects()
            projects.forEach((project: { projectId: string; displayName: string; }) => {
                layer.displayString = layer.displayString.replace(new RegExp(`\\b${project.projectId}\\b`, 'g'), project.displayName);
            });
        }

        // Recursively apply the same replacement logic to child layers
        await replaceModuleInstanceIdsWithNames(layer);
    }
}

/**
 * Reduce the depth of the layer tree, if the layer has only one child
 * @param layer
 */
function reduceLayerDepth(layer: SourceLayer) {
    // layer.children.forEach((child) => {
    //     reduceLayerDepth(child)
    // })
    //
    // // layers that should not be merged
    // const neverMerge = ["doop", "DevSecOps", "DPAS", "KeyChanger", "idms", "hub"]
    //
    // // if the layer has only one child, merge the child into the parent
    // if (layer.children.length === 1 && !neverMerge.some(x => x.toLowerCase() === layer.name.toLowerCase())) {
    //     layer.name = layer.name + " / " + layer.children[0].name
    //     layer.numberOfWarningEvents = layer.children[0].numberOfWarningEvents
    //     layer.numberOfErrorEvents = layer.children[0].numberOfErrorEvents
    //     layer.numberOfInfoEvents = layer.children[0].numberOfInfoEvents
    //     layer.show = layer.children[0].show
    //     if (layer.children[0].source !== undefined) {
    //         layer.source = layer.children[0].source
    //     }
    //     layer.children = layer.children[0].layer
    // }
}

export function fixModuleName(name: string) {
    switch (name.toLowerCase()) {
        case "dams / devsecops":
            return "All modules"
        case "doop":
            return "DOOP"
        case "hub":
            return "Hub"
        case "idms":
            return "IDMS"
        default:
            return name
    }
}